import { useState } from 'react';
import useWorkflowStore from '@/stores/WorkflowStore';
import { Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { validateChannelCompatibility } from '@/lib/channelCapabilities';
import { validateWorkflow } from '@/lib/workflowUtils';
import { updateWorkflowInstance } from '@/hooks/workflow';
import { Button } from '@/components/ui/button';

const UpdateWorkflow = ({
  workflowId,
  nodes,
  edges,
  id,
  className,
  disabled,
  ...props
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();

  // Get workflow store data
  const {
    workflowList,
    tempTrigger,
    workflowName,
    workflowDescription,
    channels,
    setWorkflowList,
  } = useWorkflowStore(
    useShallow((state) => ({
      workflowList: state.workflowList,
      tempTrigger: state.tempTrigger,
      workflowName: state.workflowName,
      workflowDescription: state.workflowDescription,
      channels: state.channels,
      setWorkflowList: state.setWorkflowList,
    }))
  );

  // Convert the ReactFlow nodes and edges to the target format
  const convertToWorkflowFormat = () => {
    // Create a map to help track the order of nodes and their connections
    const nodeConnections = {};

    // Process edges to determine connections between nodes
    edges?.forEach((edge) => {
      if (edge.source && edge.target) {
        if (!nodeConnections[edge.source]) {
          nodeConnections[edge.source] = { next: edge.target };
        } else {
          nodeConnections[edge.source].next = edge.target;
        }
      }
    });

    // Create a map of node types for quick lookup
    const nodeTypesMap = {};
    nodes.forEach((node) => {
      nodeTypesMap[node.id] = node.type;
    });

    // Map branch data nodes to their branch IDs
    const branchDataMap = {};
    nodes
      .filter((node) => node.type === 'branch-data')
      .forEach((node) => {
        branchDataMap[node.data.branchId] = node.id;
      });

    // Steps array to return
    const steps = [];
    let currentOrder = 1;

    // Find the trigger node as starting point
    const triggerNode = nodes.find((node) => node.type === 'trigger');
    if (!triggerNode) return steps;

    // Use a queue for breadth-first traversal
    const processedNodes = new Set();

    // Start traversal from the trigger
    const traverseFromNode = (nodeId) => {
      if (processedNodes.has(nodeId)) return;
      processedNodes.add(nodeId);

      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;

      // Skip trigger nodes in the steps output
      if (node.type === 'trigger') {
        // Continue with the node connected to the trigger
        const nextNodeId = nodeConnections[node.id]?.next;
        if (nextNodeId) {
          traverseFromNode(nextNodeId);
        }
        return;
      }

      // Process branch-config node
      if (node.type === 'branch-config') {
        const branchStep = {
          id: node.id,
          type: 'branch',
          order: currentOrder++,
          branches: [],
        };

        // Find the default branch
        const defaultBranch = node.data.branches.find(
          (b) => !b.conditions || b.conditions.length === 0
        );
        if (defaultBranch) {
          branchStep.default_branch_id = defaultBranch.id;
        }

        // Process each branch
        node.data.branches.forEach((branch) => {
          const branchData = {
            id: branch.id,
            name: branch.name,
          };

          // Get the branch data node
          const branchDataNodeId = branchDataMap[branch.id];

          if (branchDataNodeId) {
            // Find next node from branch data node
            const nextNodeId = nodeConnections[branchDataNodeId]?.next;
            if (nextNodeId && !nodeTypesMap[nextNodeId]?.startsWith('end')) {
              branchData.next_step_id = nextNodeId;
            }
          }

          // Set default branch flag or add conditions
          if (branch.id === branchStep.default_branch_id) {
            branchData.default_branch = true;
          } else if (branch.conditions && branch.conditions.length > 0) {
            branchData.condition = convertConditions(branch.conditions);
          }

          branchStep.branches.push(branchData);
        });

        steps.push(branchStep);

        // Process each branch's connected nodes
        node.data.branches.forEach((branch) => {
          const branchDataNodeId = branchDataMap[branch.id];
          if (branchDataNodeId) {
            const nextNodeId = nodeConnections[branchDataNodeId]?.next;
            if (nextNodeId && !nodeTypesMap[nextNodeId]?.startsWith('end')) {
              traverseFromNode(nextNodeId);
            }
          }
        });
      }
      // Process action node
      else if (node.type === 'action') {
        const actionType = node.data.actionType;
        const step = {
          id: node.id,
          type: actionType === 'delay' ? 'delay' : 'action',
          order: currentOrder++,
        };

        // Handle specific action types
        if (actionType === 'delay') {
          step.delay = node.data.config?.value || 0;
          step.delay_unit = node.data.config?.unit || 'seconds';
        } else if (actionType === 'send_message') {
          step.actions = [
            {
              type: 'send_message',
              value: node.data.config?.message || '',
            },
          ];
        } else if (actionType === 'reply_comment') {
          step.actions = [
            {
              type: 'reply_comment',
              value: node.data.config?.comment || '',
            },
          ];
        } else if (actionType === 'contact_add_tag') {
          step.actions = [
            {
              type: 'contact_add_tag',
              value: node.data.config?.tags || [],
            },
          ];
        } else if (actionType === 'contact_remove_tag') {
          step.actions = [
            {
              type: 'contact_remove_tag',
              value: node.data.config?.tags || [],
            },
          ];
        } else if (actionType === 'contact_change_owner') {
          if (node.data.config?.ownerMode === 'multiple') {
            step.actions = [
              {
                type: 'contact_change_owner',
                value: node.data.config?.owners || [],
                operator: 'weighted_allocation',
              },
            ];
          } else {
            step.actions = [
              {
                type: 'contact_change_owner',
                value: node.data.config?.owner || '',
              },
            ];
          }
        } else if (actionType === 'contact_state_change') {
          step.actions = [
            {
              type: 'contact_state_change',
              value: node.data.config?.state || '',
            },
          ];
        } else if (actionType === 'trigger_webhook') {
          step.actions = [
            {
              type: 'trigger_webhook',
              value: {
                url: node.data.config?.url || '',
                method: node.data.config?.method || 'POST',
              },
            },
          ];
        } else if (actionType === 'ai_select_message') {
          step.actions = [
            {
              type: 'ai_select_message',
              value: {
                messages: node.data.config?.messages || [],
                custom_prompt: node.data.config?.custom_prompt || '',
              },
            },
          ];
        } else if (actionType === 'linkedin_reply_comment') {
          step.actions = [
            {
              type: 'linkedin_reply_comment',
              value: node.data.config?.comment || '',
            },
          ];
        } else {
          // Handle other action types
          step.actions = [
            {
              type: actionType || 'unknown',
              value: node.data.config || {},
            },
          ];
        }

        // Add next step ID if exists and isn't an end node
        const nextNodeId = nodeConnections[node.id]?.next;
        if (nextNodeId && !nodeTypesMap[nextNodeId]?.startsWith('end')) {
          step.next_step_id = nextNodeId;
        }

        steps.push(step);

        // Continue traversal with the next node
        if (nextNodeId && !nodeTypesMap[nextNodeId]?.startsWith('end')) {
          traverseFromNode(nextNodeId);
        }
      }
    };

    // Start traversal from the trigger node
    traverseFromNode(triggerNode.id);

    return steps;
  };

  // Helper function to convert conditions
  const convertConditions = (conditions) => {
    if (!conditions || conditions.length === 0) {
      return undefined;
    }

    // For a single condition array
    if (conditions.length === 1 && conditions[0].length === 1) {
      const condition = conditions[0][0];
      return {
        type: 'condition',
        field: condition.field,
        operator: condition.operator,
        value: condition.value,
      };
    }

    // For multiple conditions (group)
    return {
      type: 'group',
      operator: 'AND', // Default to AND, adjust if needed
      conditions: conditions.flatMap((conditionGroup) =>
        conditionGroup.map((condition) => ({
          type: 'condition',
          field: condition.field,
          operator: condition.operator,
          value: condition.value,
        }))
      ),
    };
  };

  const handleUpdateWorkflow = () => {
    setIsUpdating(true);

    try {
      const workflowSteps = convertToWorkflowFormat();

      // Validate with the generated steps using the shared validator
      const validationErrors = validateWorkflow(
        workflowSteps,
        tempTrigger,
        channels
      );

      if (validationErrors.length > 0) {
        // Display toast with validation errors
        validationErrors.forEach((error) => {
          toast.error(error);
        });
        setIsUpdating(false);
        return;
      }

      // Validate channel compatibility
      const channelCompatibility = validateChannelCompatibility(
        tempTrigger,
        nodes,
        channels
      );

      if (!channelCompatibility.isValid) {
        // Display single toast with all channel compatibility issues
        toast.error(
          <div className='flex flex-col gap-1'>
            <span className='font-medium'>Channel compatibility issues:</span>
            {channelCompatibility.issues.map((issue, index) => {
              const channelList = issue.unsupportedChannels.join(', ');
              return (
                <span key={index} className='text-sm'>
                  • {issue.label} is not supported by: {channelList}
                </span>
              );
            })}
          </div>
        );
        setIsUpdating(false);
        return;
      }

      // Create workflow object
      const workflow = {
        id: workflowId,
        name: workflowName || 'New Workflow',
        description: workflowDescription || '',
        channel: channels,
        triggers: tempTrigger,
        steps: workflowSteps,
      };

      // Use updateWorkflowInstance with toast promise
      toast.promise(updateWorkflowInstance(workflowId, workflow), {
        loading: 'Updating workflow...',
        success: (response) => {
          // Update workflow store
          setWorkflowList({
            ...workflowList,
            [workflow.id]: workflow,
          });
          setIsUpdating(false);
          // Navigate to workflows page after successful update
          navigate('/workflows');
          return 'Workflow updated successfully!';
        },
        error: (error) => {
          console.error('Error updating workflow:', error);
          setIsUpdating(false);
          const serverMessage = error.response?.data?.message;
          if (
            serverMessage?.includes(
              "Non-default branch requires a 'next_step_id'"
            )
          ) {
            return 'At least one action is required under each branch';
          }
          return serverMessage || 'Failed to update workflow';
        },
      });
    } catch (error) {
      console.error('Error preparing workflow:', error);
      toast.error('Error preparing workflow data');
      setIsUpdating(false);
    }
  };

  return (
    <Button
      id={id}
      onClick={handleUpdateWorkflow}
      disabled={disabled || isUpdating}
      className={className}
      {...props}
    >
      <Edit className='mr-2 h-4 w-4' />
      {isUpdating ? 'Updating...' : 'Update Workflow'}
    </Button>
  );
};

export default UpdateWorkflow;
